import React, { FC, useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'

import {
  handlers,
  getErrorMessage,
} from '@kaidam/fc-shared/src/frontend/store/utils/api'
import {
  validatePostcode,
} from '@kaidam/fc-shared/src/booking/utils'

import { snackbarMessage as noCoverageMessage } from '../components/widgets/NoCoverageMessage'
import { snackbarMessage as offSeasonMessage } from '../components/widgets/OffSeasonMessage'

import useOrgs from '../hooks/useOrgs'
import useConfig from '../hooks/useConfig'

import networkActions from '@kaidam/fc-shared/src/frontend/store/modules/network'
import snackbarActions from '@kaidam/fc-shared/src/frontend/store/modules/snackbar'
import routerActions from '@kaidam/fc-shared/src/frontend/store/modules/router'
import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'

const Home: FC<React.PropsWithChildren<unknown>> = ({}) => {
  const dispatch = useDispatch()
  const orgs = useOrgs()
  const config = useConfig()

  const [ errorMessage, setErrorMessage ] = useState('')
  const [ postcode, setPostcode ] = useState('')

  const params = useSelector(routerSelectors.params)

  const searchForPostcode = useCallback(async (postcode: string) => {
    dispatch(networkActions.setGlobalLoading({
      active: true,
    }))
    try {
      if(!postcode) {
        dispatch(snackbarActions.setError(`please enter a postcode`))
        return
      }
      if(!validatePostcode(postcode)) {
        dispatch(snackbarActions.setError(`invalid postcode: ${postcode}`))
        return
      }
      const data = await handlers.get<{
        org: string,
        ets: string[],
        is_off_season: boolean,
      }>(`/ft_booking_public/match_postcode`, {
        params: {
          postcode,
        },
      })
      
      if(!data.org) {
        dispatch(snackbarActions.setInfo(noCoverageMessage))
        dispatch(routerActions.navigateTo('register_interest', {
          postcode,
        }))
      } else if(data.is_off_season) {
        dispatch(routerActions.navigateTo('register_interest', {
          postcode,
          org: data.org,
          is_off_season: 'yes',
        }))
      }
      else {
        localStorage.removeItem('booking')
        localStorage.setItem('postcode', postcode)
        dispatch(routerActions.navigateTo('booking.welcome', {
          orgid: data.org,
        }))
      }
    } catch(e) {
      dispatch(snackbarActions.setError(getErrorMessage(e)))
    }
    dispatch(networkActions.setGlobalLoading({
      active: false,
    }))
  }, [])

  useEffect(() => {
    if(params.postcode) {
      searchForPostcode(params.postcode)
    }
  }, [])

  if(!config.loaded) {
    return null
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{
        display: 'flex',
        padding: 2
      }}
    >
      <Paper 
        sx={{
          width: '100%',
          padding: 3
        }}
      >
        {
          config.isOffSeason ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={ () => {
                dispatch(routerActions.navigateTo('register_interest', {is_off_season:'yes'}))
              } }
            >
              Register Interest In A Survey
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={ () => {
                dispatch(routerActions.navigateTo('login'))
              } }
            >
              Manage Existing Booking
            </Button>
          )
        }
        <Divider sx={{ my: 2 }} />
        <Typography
          color="primary"
          align="left"
          gutterBottom
          variant="h6"
          sx={{ pb: 2 }}
        >
          Enter Your Postcode
        </Typography>
        <Typography
          align="left"
          gutterBottom
          variant="body1"
          sx={{ pb: 2 }}
        >
          To begin the booking process - enter your postcode and we will find the surveyors closest to you:
        </Typography>
        <Grid container spacing={ 1 }>
          <Grid item xs={ 12 } sm={ 3 }>
            <TextField
              id="postcode"
              label="Enter your postcode"
              fullWidth
              value={ postcode }
              sx={{ mb: 2 }}
              onChange={ e => setPostcode(e.target.value) }
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 9 }>
            <Button
              variant="outlined"
              color="primary"
              onClick={ () => {
                searchForPostcode(postcode)
              } }
            >
              Find Surveyors
            </Button>
          </Grid>
        </Grid>
        {
          errorMessage && (
            <Alert severity="error">{ errorMessage }</Alert>
          )
        }
      </Paper>
    </Container>
  )
}

export default Home